import React, { useEffect, useState } from "react";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import {
  Box,
  Grid,
  Button,
  Tooltip,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Divider,
  Avatar,
  Stack,
} from "@mui/material";
import moment from "moment";
import dayjs from "dayjs";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { apiData } from "../../../services/api_service";
import { fetchCustomerStatementAPI } from "../../../config";
import Layout from "../../../layout/Layout";
import { formatNumber, formatAED } from "../../../utils/format";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

// payment_state (Invoice rows only) -> chip color.
const STATUS_COLOR = {
  Paid: "success",
  // Some received (advance / Settle Payment), balance still due.
  "Partly Paid": "warning",
  Pending: "error",
};

// Type label: distinguish a Credit Invoice (payment_terms === "Credit", i.e.
// raised with credit days, settled later) from a plain immediate/cash
// Invoice, so it's clear at a glance why a row is still Pending vs Paid.
const getTypeLabel = (t) =>
  t.txn_type === "Invoice" && t.payment_terms === "Credit" ? "Credit Invoice" : t.txn_type;

// Type and Status are related but not fully redundant — a Credit Invoice
// can later become Paid too — so instead of two columns, one chip carries
// both: its label is the type, its color is the payment status. Credit
// Note rows have no payment_state, so they fall back to a neutral color.
const getRowChipColor = (t) =>
  t.txn_type === "Invoice"
    ? STATUS_COLOR[t.payment_state] || "default"
    : t.txn_type === "Receipt"
    ? "success"
    : "warning";

// payment_method already comes back as a human-readable label ("Cash",
// "Card", "Bank Transfer", ...) — orderMasterModel.js's resolvePaymentMethodLabel
// resolves the form's numeric option id to text before it's ever stored, so
// there's no code here to map. Null just means nothing's been paid yet
// (e.g. a still-pending Credit invoice, or a Credit Note, which has no
// payment method at all).
const getPaymentMethodLabel = (t) => t.payment_method || "-";

// Summary card definitions — icon + accent color kept together so the card
// row and any future reuse (e.g. a print/export summary) stay in sync.
const SUMMARY_CARDS = [
  { key: "openingBalance", label: "Opening Balance", icon: AccountBalanceWalletOutlinedIcon, accent: "#64748b" },
  { key: "totalInvoiced", label: "Total Invoiced", icon: ReceiptLongOutlinedIcon, accent: "#1976d2" },
  { key: "totalReceived", label: "Total Received", icon: PaymentsOutlinedIcon, accent: "#2e7d32" },
  // { key: "totalCreditNotes", label: "Credit Notes", icon: AssignmentReturnOutlinedIcon, accent: "#ed6c02" },
  { key: "closingBalance", label: "Closing Balance (Pending)", icon: ErrorOutlineOutlinedIcon, accent: "#d32f2f", highlight: true },
];

const CustomerStatementDetail = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();

  // Carry over whatever range was selected on the list page (passed as
  // ?from=&to= when the row is clicked) instead of silently resetting to
  // the current month — that made the detail page disagree with the filter
  // the user had just applied. Only fall back to "current month" when
  // there's nothing in the URL (e.g. a direct link/bookmark).
  // The list always sends both params; an EMPTY value means that date was
  // cleared there (all-time), so it stays cleared here too.
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const [fromDate, setFromDate] = useState(() =>
    searchParams.has("from")
      ? fromParam
        ? dayjs(fromParam)
        : null
      : dayjs().startOf("month")
  );
  const [toDate, setToDate] = useState(() =>
    searchParams.has("to")
      ? toParam
        ? dayjs(toParam)
        : null
      : dayjs().endOf("month")
  );

  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [searched, setSearched] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Accepts optional overrides so a "Clear" action can re-fetch with the
  // just-cleared values immediately — reading fromDate/toDate from state
  // right after setFromDate/setToDate would still see the pre-update
  // values (React state updates aren't synchronous), silently re-running
  // the *old* filtered query instead of the cleared one.
  const fetchStatement = async (overrides = {}) => {
    if (!customerId) return;
    try {
      setLoading(true);
      setSearched(true);
      const effectiveFromDate = "fromDate" in overrides ? overrides.fromDate : fromDate;
      const effectiveToDate = "toDate" in overrides ? overrides.toDate : toDate;
      const payload = {
        customer_id: customerId,
        fromDate: effectiveFromDate ? dayjs(effectiveFromDate).format("YYYY-MM-DD") : null,
        toDate: effectiveToDate ? dayjs(effectiveToDate).format("YYYY-MM-DD") : null,
      };
      const res = await apiData("get", fetchCustomerStatementAPI, payload);
      if (res?.success === false) {
        setErrorMessage(res?.msg || "Failed to fetch statement of account");
        setStatement(null);
        return;
      }
      setStatement(res?.data || null);
    } catch (error) {
      console.error("Failed to fetch statement of account", error);
      setErrorMessage("Failed to fetch statement of account");
      setStatement(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load the statement for this customer as soon as we land on the
  // page (clicked in from the customer list) or the customer changes.
  useEffect(() => {
    fetchStatement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleClear = () => {
    const resetToDate = dayjs();
    setFromDate(null);
    setToDate(resetToDate);
    fetchStatement({ fromDate: null, toDate: resetToDate });
  };

  const handleExportData = () => {
    if (!statement || !statement.transactions?.length) {
      setErrorMessage("No data to export!");
      return;
    }

    // Mirrors the on-screen table exactly — same columns, same order, same
    // three sections (opening balance / transactions / totals) — so the
    // Excel export never drifts from what's actually shown on the page.
    const rows = [
      {
        Date: "",
        Type: "",
        "Invoice No. / Reference No.": "Opening Balance",
        "Ref. (CDN/Invoice)": "",
        "Payment Method": "",
        "Due Date": "",
        "Overdue Days": "",
        Debit: "",
        Credit: "",
        "Running Balance": Number(statement.openingBalance) || 0,
      },
      ...statement.transactions.map((t) => ({
        Date: t.txn_date ? moment(t.txn_date).format("DD-MM-YYYY") : "",
        Type: getTypeLabel(t),
        "Invoice No. / Reference No.": t.reference_no || "",
        "Ref. (CDN/Invoice)": t.related_reference || "",
        "Payment Method": getPaymentMethodLabel(t),
        "Due Date": t.due_date ? moment(t.due_date).format("DD-MM-YYYY") : "",
        "Overdue Days": t.days_overdue > 0 ? Number(t.days_overdue) : "",
        Debit: t.debit ? Number(t.debit) : "",
        Credit: t.credit ? Number(t.credit) : "",
        "Running Balance": Number(t.running_balance) || 0,
      })),
      {
        Date: "",
        Type: "",
        "Invoice No. / Reference No.": "Totals / Closing Balance",
        "Ref. (CDN/Invoice)": "",
        "Payment Method": "",
        "Due Date": "",
        "Overdue Days": "",
        Debit: Number(statement.summary?.totalInvoiced) || 0,
        Credit: Number(statement.summary?.totalCredit) || 0,
        "Running Balance": Number(statement.closingBalance) || 0,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    // Debit / Credit / Running Balance are real numbers, shown with two decimals.
    Object.keys(ws).forEach((addr) => {
      if (/^[HIJ]\d+$/.test(addr) && !/^[HIJ]1$/.test(addr) && ws[addr].t === "n") ws[addr].z = "#,##0.00";
    });
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Statement of Account");

    // Who and which period the statement is for — kept off the table sheet so its header row stays frozen.
    const fmtP = (d) => (d ? moment(d.toDate ? d.toDate() : d).format("DD-MM-YYYY") : "Beginning");
    const info = XLSX.utils.aoa_to_sheet([
      ["Customer", statement.customer?.name || ""],
      ["Period From", fmtP(fromDate)],
      ["Period To", fmtP(toDate)],
      ["Opening Balance", Number(statement.openingBalance) || 0],
      ["Total Invoiced", Number(statement.summary?.totalInvoiced) || 0],
      ["Total Received", Number(statement.summary?.totalCredit) || 0],
      ["Closing Balance", Number(statement.closingBalance) || 0],
    ]);
    ["B4", "B5", "B6", "B7"].forEach((a) => {
      if (info[a]) info[a].z = "#,##0.00";
    });
    fitColumns(info);
    XLSX.utils.book_append_sheet(wb, info, "Summary");

    const fileName = `SOA_${(statement.customer?.name || "customer").replace(
      /\s+/g,
      "_"
    )}_${moment().format("DD_MM_YYYY")}.xlsx`;
    writeXlsxFrozen(XLSX, wb, fileName);
    setSuccessMessage("Excel Downloaded Successfully!");
  };

  const summary = statement?.summary || {};
  const cardValues = { ...summary, openingBalance: statement?.openingBalance, closingBalance: statement?.closingBalance };
  const customerInitial = (statement?.customer?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%", backgroundColor: "#28a745", color: "#fff", fontWeight: 600 }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Layout's top bar (Header.jsx) is position:fixed with a 70px
          Toolbar and contributes 0 height to normal flow, so every page
          not using the legacy `.employee-form` class (which bakes in 3%
          padding-top) needs its own clearance or the fixed bar covers the
          top of the content — pt: "85px" matches the value already used
          for the same reason in SalesExecDashboard.jsx. */}
      <Box sx={{ pt: "85px", px: { xs: 2, md: 3.5 }, pb: 4 }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Statement of Account
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 0.25 }}>
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link
                underline="hover"
                color="inherit"
                href="/reports/customer-statement"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                Statement of Account
              </Link>
              <Typography color="text.primary">{statement?.customer?.name || "..."}</Typography>
            </Breadcrumbs>
          </Box>

          <Stack direction="row" spacing={1}>
            {statement && (
              <Tooltip title="Export statement to Excel" arrow>
                <Button
                  onClick={handleExportData}
                  variant="outlined"
                  color="success"
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Export
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Back to List
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "primary.main", width: 38, height: 38 }}>
                <DomainOutlinedIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap title={statement?.customer?.name}>
                  {statement?.customer?.name || (loading ? "Loading..." : "-")}
                </Typography>
                {statement?.customer?.phone_number && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {statement.customer.phone_number}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="From Date"
                value={fromDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setFromDate(newValue)}
                renderInput={(params) => (
                  <Tooltip title="Statement start date (leave blank for all-time)" arrow>
                    <TextField {...params} size="small" fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="To Date"
                value={toDate}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => setToDate(newValue)}
                renderInput={(params) => (
                  <Tooltip title="Statement end date" arrow>
                    <TextField {...params} size="small" fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => fetchStatement()}
                disabled={loading}
                fullWidth
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {loading ? "Loading..." : "Search"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                fullWidth
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {statement && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Period: <b>{statement.fromDate ? moment(statement.fromDate).format("DD-MM-YYYY") : "Beginning"}</b>
            {" → "}
            <b>{statement.toDate ? moment(statement.toDate).format("DD-MM-YYYY") : "Today"}</b>
            {"  ·  Generated "}
            {moment().format("DD-MM-YYYY HH:mm")}
          </Typography>
        )}
      </Paper>

      {searched && !loading && !statement && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <InboxOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No statement data found for this customer / date range.
          </Typography>
        </Box>
      )}

      {statement && (
        <>
          {/* Summary cards. Plain CSS grid, not MUI's <Grid container>:
              MUI's legacy Grid puts a negative margin on the container for
              its own gutter system, which shrinks how much height that
              container contributes to ITS parent's auto-height — so a
              taller wrapped card (e.g. "Closing Balance (Pending)"'s
              2-line label) can visually render a few px below where the
              parent thinks its own bottom edge is, regardless of what
              wraps it (confirmed via getBoundingClientRect, not a guess).
              CSS grid + gap has no such margin, so this class of overlap
              can't happen. */}
          <Box
            sx={{
              mt: 1.5,
              mb: 2,
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
            }}
          >
            {SUMMARY_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <Paper
                  key={card.key}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 3,
                    overflow: "hidden",
                    borderLeft: `4px solid ${card.accent}`,
                    backgroundColor: card.highlight ? "rgba(211,47,47,0.04)" : "#fff",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Icon sx={{ fontSize: 16, color: card.accent }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {card.label}
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: card.highlight ? card.accent : "text.primary" }}>
                    {formatAED(cardValues[card.key])}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* How the figures above reconcile to the closing balance */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, mb: 1.5 }}>
            Opening {formatAED(cardValues.openingBalance)} + Invoiced {formatAED(cardValues.totalInvoiced)} − Received {formatAED(cardValues.totalReceived)}
            {Number(summary?.totalCdnAdvances) > 0 ? ` − CDN advances ${formatAED(summary.totalCdnAdvances)}` : ""}
            {Number(summary?.totalCreditNotes) > 0 ? ` − Credit notes ${formatAED(summary.totalCreditNotes)}` : ""}
            {" = "}
            <b>Closing {formatAED(cardValues.closingBalance)}</b>
          </Typography>

          {/* Ageing of what the customer still owes, as of today (all dates) */}
          {statement.ageing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 0.75 }}>
                OUTSTANDING AGEING (as of {moment(statement.ageing.as_of).format("DD-MM-YYYY")}, from invoice due date)
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
                }}
              >
                {[
                  { key: "current", label: "Current (not due)", accent: "#2e7d32" },
                  { key: "d1_30", label: "1 - 30 days overdue", accent: "#f9a825" },
                  { key: "d31_60", label: "31 - 60 days overdue", accent: "#ef6c00" },
                  { key: "d61_90", label: "61 - 90 days overdue", accent: "#d84315" },
                  { key: "d90_plus", label: "Over 90 days overdue", accent: "#b71c1c" },
                ].map((b) => (
                  <Paper
                    key={b.key}
                    variant="outlined"
                    sx={{ p: 1.25, borderRadius: 3, borderLeft: `4px solid ${b.accent}`, backgroundColor: "#fff" }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block" }}>
                      {b.label}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: statement.ageing.buckets[b.key] > 0 ? b.accent : "text.disabled" }}>
                      {formatAED(statement.ageing.buckets[b.key])}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              {statement.ageing.unapplied_credit_notes > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  Invoices outstanding {formatAED(statement.ageing.invoices_outstanding)} less credit notes{" "}
                  {formatAED(statement.ageing.unapplied_credit_notes)} = net {formatAED(statement.ageing.net_outstanding)}
                </Typography>
              )}
            </Box>
          )}

          {/* Transaction table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* Header color/weight is applied globally (see
                      tableEnhancements.css — every read-only list/report
                      table gets the same navy header for consistency), so
                      only the per-column alignment needs setting here. */}
                  {["Date", "Type", "Invoice No. / Reference No.", "Ref. (CDN/Invoice)", "Payment Method", "Due Date", "Overdue Days", "Debit", "Credit", "Running Balance"].map(
                    (head, i) => (
                      <TableCell key={head} align={i >= 7 ? "right" : "left"}>
                        {head}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* !important: the global zebra rule (tableEnhancements.css)
                    sets background-color on every row by :nth-of-type, which
                    otherwise wins over a plain sx override here on
                    specificity — these two summary rows need to visually
                    stand out from ordinary transaction rows regardless. */}
                <TableRow sx={{ backgroundColor: "#eceff1 !important" }}>
                  <TableCell colSpan={9} sx={{ fontWeight: 700, color: "text.secondary" }}>
                    Opening Balance
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatAED(statement.openingBalance)}
                  </TableCell>
                </TableRow>

                {statement.transactions.map((t, index) => (
                  <TableRow
                    key={`${t.txn_type}-${t.reference_no}-${index}`}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell>{moment(t.txn_date).format("DD-MM-YYYY")}</TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(t)}
                        size="small"
                        color={getRowChipColor(t)}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t.reference_pdf_url ? (
                        <a
                          href={t.reference_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
                        >
                          {t.reference_no}
                        </a>
                      ) : (
                        t.reference_no
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {t.related_reference ? (
                        t.related_reference_pdf_url ? (
                          <a
                            href={t.related_reference_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "none" }}
                          >
                            {t.related_reference}
                          </a>
                        ) : (
                          t.related_reference
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {getPaymentMethodLabel(t)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {t.due_date ? moment(t.due_date).format("DD-MM-YYYY") : "-"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: t.days_overdue > 0 ? 700 : 400, color: t.days_overdue > 0 ? "error.main" : "text.secondary" }}>
                      {t.days_overdue > 0 ? `${formatNumber(t.days_overdue)} days` : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {t.debit ? formatAED(t.debit) : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: t.credit ? "success.main" : "inherit" }}>
                      {t.credit ? formatAED(t.credit) : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                      {formatAED(t.running_balance)}
                    </TableCell>
                  </TableRow>
                ))}

                {!statement.transactions.length && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No transactions in the selected date range.
                    </TableCell>
                  </TableRow>
                )}

                <TableRow sx={{ backgroundColor: "#e3f2fd !important" }}>
                  <TableCell colSpan={7} sx={{ fontWeight: 700 }}>
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatAED(summary.totalInvoiced)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatAED(summary.totalCredit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "primary.dark" }}>
                    Closing: {formatAED(statement.closingBalance)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      </Box>
    </Layout>
  );
};

export default CustomerStatementDetail;
